import {
  deleteV1CompaniesCompanyIdOmniReportsId,
  deleteV1CompaniesCompanyIdReportCategoriesId,
  getV1CompaniesCompanyIdOmniReportsId,
  getV1CompaniesCompanyIdReportCategories,
  GetV1CompaniesCompanyIdReportCategoriesData,
  getV1CompaniesCompanyIdReportCategoriesReportCategoryIdOmniReports,
  GetV1CompaniesCompanyIdReportCategoriesReportCategoryIdOmniReportsData,
  patchV1CompaniesCompanyIdOmniReportsId,
  patchV1CompaniesCompanyIdReportCategoriesId,
  postV1CompaniesCompanyIdOmniReports,
  postV1CompaniesCompanyIdReportCategories,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useState } from 'react';

import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';

import { OmniReport, ReportCategory } from './models';

export const useOmniReports = () => {
  const [isLoadingReport, setIsLoadingReport] = useState(false);

  const getCompanyOmniReportById = async (companyId: string, id: string) => {
    setIsLoadingReport(true);

    try {
      const resp = await getV1CompaniesCompanyIdOmniReportsId({
        path: { 'company-id': companyId, id },
      });
      return resp.data.data.embed_url;
    } catch (error) {
      connection.handleRequestError(error);
      console.error(error);
      throw new Error(`Unable to retrieve omni report ${id}`);
    } finally {
      setIsLoadingReport(false);
    }
  };

  interface GetReportCategoriesParams {
    companyId: string;
    limit?: number;
    cursor?: string;
  }

  const getReportCategories = async ({
    companyId,
    limit,
    cursor,
  }: GetReportCategoriesParams) => {
    const params: Partial<GetV1CompaniesCompanyIdReportCategoriesData['query']> = {
      'page[limit]': limit ?? 20,
    };

    if (cursor) {
      params['page[after]'] = cursor;
    }

    try {
      const response = await getV1CompaniesCompanyIdReportCategories({
        path: { 'company-id': companyId },
        query: params,
      });

      const data = response.data.data.map(ReportCategory.parse);
      const pagination = extractPagination(response);
      return { data, pagination };
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.omni.failed_to_fetch_report_categories'),
      );
    }
  };

  const createReportCategory = async (
    companyId: string,
    name: string,
    system: boolean,
  ) => {
    try {
      const resp = await postV1CompaniesCompanyIdReportCategories({
        path: { 'company-id': companyId },
        body: { name, system },
      });

      const reportCategory = ReportCategory.parse(resp.data.data);
      return reportCategory;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      console.error(error);
      connection.handleRequestError(error);
    }
  };

  const updateReportCategory = async (companyId: string, id: string, name: string) => {
    try {
      const resp = await patchV1CompaniesCompanyIdReportCategoriesId({
        path: { 'company-id': companyId, id },
        body: { name },
      });

      const reportCategory = ReportCategory.parse(resp.data.data);
      return reportCategory;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      console.error(error);
      connection.handleRequestError(error);
    }
  };

  const deleteReportCategory = async (companyId: string, id: string) => {
    try {
      await deleteV1CompaniesCompanyIdReportCategoriesId({
        path: { 'company-id': companyId, id },
      });
    } catch (error) {
      console.error(error);
      connection.handleRequestError(error);
    }
  };

  interface GetOmniReportsByReportCategoryParams {
    companyId: string;
    reportCategoryId: string;
    limit?: number;
    cursor?: string;
    signal?: AbortSignal;
  }
  const getOmniReportsByReportCategory = async ({
    companyId,
    reportCategoryId,
    limit,
    cursor,
    signal,
  }: GetOmniReportsByReportCategoryParams) => {
    const params: Partial<
      GetV1CompaniesCompanyIdReportCategoriesReportCategoryIdOmniReportsData['query']
    > = {
      'page[limit]': limit ?? 20,
    };

    if (cursor) {
      params['page[after]'] = cursor;
    }

    try {
      const response =
        await getV1CompaniesCompanyIdReportCategoriesReportCategoryIdOmniReports({
          path: { 'company-id': companyId, 'report-category-id': reportCategoryId },
          query: params,
          signal,
        });

      const data = response.data.data.map(OmniReport.parse);
      const pagination = extractPagination(response);
      return { data, pagination };
    } catch (error) {
      if (signal?.aborted) {
        throw error;
      }

      connection.handleRequestError(
        error,
        t('error_messages.omni.failed_to_fetch_omni_reports'),
      );
    }
  };

  const createOmniReport = async (companyId: string, omniReport: OmniReport) => {
    try {
      const body = OmniReport.deparse(omniReport);

      const resp = await postV1CompaniesCompanyIdOmniReports({
        path: { 'company-id': companyId },
        body,
      });

      const report = OmniReport.parse(resp.data.data);
      return report;
    } catch (error) {
      console.error(error);
      connection.handleRequestError(
        error,
        t('error_messages.omni.failed_to_create_omni_report'),
      );
    }
  };

  const updateOmniReport = async (companyId: string, omniReport: OmniReport) => {
    try {
      const body = OmniReport.deparseUpdate(omniReport);

      const resp = await patchV1CompaniesCompanyIdOmniReportsId({
        path: { 'company-id': companyId, id: omniReport.id },
        body,
      });

      const report = OmniReport.parse(resp.data.data);
      return report;
    } catch (error) {
      console.error(error);
      connection.handleRequestError(
        error,
        t('error_messages.omni.failed_to_update_omni_report'),
      );
    }
  };

  const deleteOmniReport = async (companyId: string, id: string) => {
    try {
      await deleteV1CompaniesCompanyIdOmniReportsId({
        path: { 'company-id': companyId, id },
      });
    } catch (error) {
      console.error(error);
      connection.handleRequestError(
        error,
        t('error_messages.omni.failed_to_delete_omni_report'),
      );
    }
  };

  return {
    isLoadingReport,
    getCompanyOmniReportById,
    getReportCategories,
    createReportCategory,
    updateReportCategory,
    deleteReportCategory,
    getOmniReportsByReportCategory,
    createOmniReport,
    updateOmniReport,
    deleteOmniReport,
  };
};
